import React, { useState } from 'react';
import { permissionObj } from '../../../../Helpers/permission';
import { statusBorderColor, statusColor, statusFontColor } from '../../../../enums/statusColorEnum';
import { status, STATUS_MODEL } from '../../../../enums/statusEnum';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomTh from '../../../../components/table/CustomTh';
import CustomTd from '../../../../components/table/CustomTd';
import DropDownMenuProfile from '../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../components/DropDown/DropDownMenuItem';
import TableFunction from '../../../../components/table/TableFunction';
import CustomChip from '../../../../components/CustomChip';
import { EMPTY_VALUES, errorMsg, getDateFormat, getFullName, getTimeFormat } from '../../../../Apis/functions';
import { MODELS } from '../../../../Apis/Models';
import { TABLES } from '../../../../Apis/Tables';
import {
    SimpleSearch,
    SimpleSearchWithId,
} from '../../../../reduxToolkit/Search/SearchApi';
import { DeleteItemsApi } from '../../../../reduxToolkit/Commons/CommonsApi';
import routes from '../../../../Routes/routes';
import {
    CancelIconsOptions,
    DeliveryBoxIcon,
    DetailsIconsOptions,
    IconClockTable,
    IconDocumentCheck,
    IconPlusFill,
    IconScheduleTable,
    PersonOutlinedChip,
    UpdateIconsOptions,
} from '../../../../components/buttons/IconButtons';
import { CRITERIA_MODEL, SEARCH_MODEL } from '../../../../components/table/Model/TableModel';
import CustomTooltip from '../../../../components/CustomTooltip';

/**
 * @email azapata@ibl.mx
 * @author Ferro
 * @jobTitle Full Stack Developer
 */

/**
 * Initializes and returns an object for managing and displaying orders in a table view.
 *
 * @param {string} search
 *   Current simple search text.
 * @param {string} supplierId
 *   Identifier of the selected supplier to filter orders by.
 * @param {string} searchElement
 *   Currently selected advanced search field name.
 * @param {string} contractorId
 *   Identifier of the selected contractor to filter orders by.
 * @param {function(Object[]): void} setCriteria
 *   Setter for the list of applied advanced search criteria.
 *   Setter for the simple search text.
 * @param {Array<any>} finalArray
 *   Array of orders after applying all filters and searches.
 *   Setter to indicate whether the data has finished loading.
 * @param {function(boolean): void} setModified
 *   Setter to mark that the orders list has been modified.
 * @param {boolean} modified
 *   Flag indicating whether the orders list has been modified since last load.
 *   Current active step in a multi-step workflow (e.g., advanced search steps).
 *   Setter for the currently selected advanced search field.
 *   Clears all advanced search criteria.
 *   Setter for the list of order IDs selected for deletion.
 * @param {function(Array<any>): void} setBodyForExport
 *   Setter for the payload array to be exported (e.g., CSV or Excel).
 *   Setter for the detailed criteria list for display or export.
 */

export const PurchaseOrderTabApp = ({
    supplierId,
    searchElement,
    contractorId,
    setModified,
    modified,
    setBodyForExport,
    setSelectedList,
    setIsAllChecked,
}) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { t } = useTranslation();
    const [tableId, setTableId] = useState('tbl_pot_purchase_orders');
    const [searchId, setSearchId] = useState('txt_pot_purchase_orders');
    const [moduleId, setModuleId] = useState(`${MODELS.Order}`);
    const [option, setOption] = useState(`${TABLES.ORDERS}`);
    const [data, setData] = useState({ content: [], totalElements: 0 });
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const deletePermission =
        (DEV || permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_DELETE)) && supplierId === null;
    const leftPosition = deletePermission ? 50 : 0;

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes='first_head'>
                {t('code')}
            </CustomTh>,
            <CustomTh tableId={tableId}>{t('item')}</CustomTh>,
            supplierId === null && <CustomTh tableId={tableId}>{t('supplier')}</CustomTh>,
            supplierId === null && <CustomTh tableId={tableId}>{t('company')}</CustomTh>,

            <CustomTh tableId={tableId}>{t('supplier_employee')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('supplier_vehicle')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('received_by')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('delivered_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
        ];
    };

    const getTbody = () => {
        if (!data || data?.content?.length === 0) return { body: [], data: [] };

        let body = data?.content?.map((item, index) => {
            const companyFullName = item.supplierCompanyAcronym
                ? `${item.supplierCompanyAcronym} | ${item.supplierCompanyName || ''}`
                : item.supplierCompanyName || EMPTY_VALUES.DEFAULT;

            const users = Array.isArray(item.userReceivedName) ? item.userReceivedName.map((u) => u.trim()) : [];

            return (
                <>
                    <CustomTd
                        tableId={tableId}
                        tooltip={item?.folio}
                        fixed={true}
                        left={leftPosition}
                        classes={'first-column'}
                    >
                        {item?.folio}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.item}</CustomTd>
                    {supplierId === null && (
                        <CustomTd tableId={tableId}>
                            {getFullName(item.supplierName, item.supplierLastName, item.supplierSecondLastName)}
                        </CustomTd>
                    )}
                    {supplierId === null && <CustomTd tableId={tableId}>{companyFullName}</CustomTd>}

                    <CustomTd tableId={tableId}>
                        {getFullName(
                            item.supplierEmployeeName,
                            item.supplierEmployeeLastName,
                            item.supplierEmployeeSecondLastName,
                        )}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {getFullName(item.supplierVehicleBrand, item.supplierVehicleSubBrand)}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {users.length === 0 ? (
                            EMPTY_VALUES.DEFAULT
                        ) : users.length <= 2 ? (
                            <div className='d-flex flex-column'>
                                {users.map((user) => (
                                    <div key={user} className='d-flex align-items-center'>
                                        <PersonOutlinedChip className='mr-8px icon-color-general-950' />
                                        <span>{user}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='d-flex align-items-center'>
                                <CustomTooltip text={users.join(', ')}>
                                    <IconPlusFill className='mr-8px icon-color-general-950' />
                                </CustomTooltip>
                                <PersonOutlinedChip className='icon-color-general-950' />
                            </div>
                        )}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className='d-flex'>
                            <div>
                                <IconScheduleTable className='mr-8px mb-1 icon-color-general-950' />
                            </div>
                            {getDateFormat(item?.deliveryData, false) || EMPTY_VALUES.DEFAULT}
                        </div>

                        <div className='d-flex'>
                            <div>
                                <IconClockTable className='mr-8px mb-1 icon-color-general-950' />
                            </div>
                            {getTimeFormat(item?.deliveryData) || EMPTY_VALUES.DEFAULT}
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <CustomChip
                            fontColor={statusFontColor[item?.statusId]}
                            borderColor={statusBorderColor[item?.statusId]}
                            background={statusColor[item?.statusId]}
                            legend={t(status[item?.statusId]) || EMPTY_VALUES.DEFAULT}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });
        return {
            body: body,
            data: data?.content,
        };
    };

const getEmployeeActions = (item, index) => {
    if (!DEV && !permission?.includes(permissionObj?.WEB_SUPPLIER_ORDER_READ)) {
        return '-';
    }

    return (
        <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
            <>
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    url={`${routes.employee.orderDetails}${item?.id}`}
                    icon={<DetailsIconsOptions />}
                />

                {item.statusId === STATUS_MODEL.ORDER_STARTED && (
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('cancel')}
                        click={() => {
                            setSelectedList([item?.id]);
                            setShowCancelModal(true);
                        }}
                        fontColor={'font-color-error-600'}
                        icon={<CancelIconsOptions />}
                        iconColor={'icon-color-error-code-600'}
                    />
                )}
            </>
        </DropDownMenuProfile>
    );
};
const getSupplierActions = (item, index) => {
    return (
        <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
            {item.statusId === STATUS_MODEL.ORDER_INCOMING && (
                <>
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('complete')}
                        url={`${routes.suppliers.completeOrder}${item?.id}`}
                        icon={<IconDocumentCheck />}
                    />
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('details')}
                        url={`${routes.suppliers.ordersDetail}${item?.id}`}
                        icon={<DetailsIconsOptions />}
                    />
                </>
            )}

            {item.statusId === STATUS_MODEL.ORDER_STARTED && (
                <>
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('update')}
                        url={`${routes.suppliers.updateOrder}${item?.id}`}
                        icon={<UpdateIconsOptions />}
                    />
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('details')}
                        url={`${routes.suppliers.ordersDetail}${item?.id}`}
                        icon={<DetailsIconsOptions />}
                    />
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('deliver')}
                        click={() => {
                            setSelectedList([item?.id]);
                            setShowDeliveryModal(true);
                        }}
                        icon={<DeliveryBoxIcon />}
                    />
                </>
            )}

            {(item.statusId === STATUS_MODEL.ORDER_CANCELED || item.statusId === STATUS_MODEL.ORDER_COMPLETE) && (
                <DropDownMenuItem
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    url={`${routes.suppliers.ordersDetail}${item?.id}`}
                    icon={<DetailsIconsOptions />}
                />
            )}
        </DropDownMenuProfile>
    );
};

const getActionsButtons = (item, index) => {
    if (!supplierId) {
        return getEmployeeActions(item, index);
    }

    return getSupplierActions(item, index);
};

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body =
            supplierId === null
                ? SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key: searchElement })
                : SEARCH_MODEL.setObject({
                      page,
                      size: rowsPerPage,
                      model: option,
                      key: searchElement,
                      userId: supplierId,
                  });

        setBodyForExport(body);
        const dispatchFunction = supplierId === null ? SimpleSearch : SimpleSearchWithId;
        dispatch(dispatchFunction({ body })).then(({payload: {data: { data, success }}}) => {
            if (success === true) {
                setData(data);
            }
        });
    }

    const {
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        openMenu,
        handleMenuToggle,
        handleClose,
        pagination,
        page,
        rowsPerPage,
    } = TableFunction({
        setSelectedList,
        data,
        getThead,
        getTbody,
        labelRowsPerPage: 'purchase_orders_per_page',
        fixedPagination: false,
        hasContainer: true,
        setIsAllChecked,
        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
    });

    const deleteSelectedOrders = (deleteItem) => {
        const tableName = TABLES.PURCHASE;
        const body = deleteItem;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                    payload: {
                        data: { success, code },
                    },
                }) => {
                    if (success === true) {
                        setSelectedList([]);
                        setIsAllChecked(false);
                        setModified(true); // Mark the data as modified
                    } else errorMsg(code);
                },
            );
        }
    };

    const cancelOrder = (id) => {};
    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        page,
        rowsPerPage,
        moduleId,
        option,
        setData,
        deletePermission,
        handleDeleteAll,
        deleteSelectedOrders,
        getActionsButtons,
        pagination,
        handleCheckboxChange,
        showDeliveryModal,
        setShowDeliveryModal,
        showCancelModal,
        setShowCancelModal,
        cancelOrder,
    };
};
